// Banners API client (MOD-10, config-tier) — düz banner (tarih-aktif pencere).
//
// Uçlar `/api/v1/banners/...`, `web.manage` policy'si. Publish iki-durum toggle.
// İş kuralları BE'de: INVALID_PLACEMENT / LANGUAGE_INACTIVE.

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import {
  normalizeConfigStatus,
  type Banner,
  type BannerDtoWire,
  type BannerInput,
  type BannerListItem,
  type BannerListItemDtoWire,
  type CreateBannerRequest,
  type UpdateBannerRequest,
} from '@features/widgets/types';

const BASE = '/api/v1/banners';

interface BannerListQuery {
  placement?: string;
  languageId?: string;
}

function mapListItem(d: BannerListItemDtoWire): BannerListItem {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    placement: d.placement ?? '',
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    status: normalizeConfigStatus(d.status),
    order: d.order ?? 0,
    startDate: d.startDate ?? null,
    endDate: d.endDate ?? null,
    updatedAt: d.updatedAt ?? '',
  };
}

function mapBanner(d: BannerDtoWire): Banner {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    placement: d.placement ?? '',
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    status: normalizeConfigStatus(d.status),
    imageUrl: d.imageUrl ?? null,
    title: d.title ?? null,
    ctaText: d.ctaText ?? null,
    ctaLink: d.ctaLink ?? null,
    order: d.order ?? 0,
    startDate: d.startDate ?? null,
    endDate: d.endDate ?? null,
    variantLanguageCodes: d.variantLanguageCodes ?? [],
  };
}

function toRequestBody(input: BannerInput): CreateBannerRequest {
  return {
    name: input.name,
    placement: input.placement,
    imageUrl: input.imageUrl ?? null,
    title: input.title ?? null,
    ctaText: input.ctaText ?? null,
    ctaLink: input.ctaLink ?? null,
    order: input.order ?? null,
    startDate: input.startDate ?? null,
    endDate: input.endDate ?? null,
  };
}

export const bannersApi = {
  async list(query: BannerListQuery = {}): Promise<BannerListItem[]> {
    const params: Record<string, string | number> = { page: 1, pageSize: 200 };
    if (query.placement) params.placement = query.placement;
    if (query.languageId) params.languageId = query.languageId;
    const res = await http.get<ActionResponse<BannerListItemDtoWire>>(BASE, { params });
    return unwrapList(res).items.map(mapListItem);
  },

  async get(id: string): Promise<Banner> {
    return mapBanner(unwrap(await http.get<ActionResponse<BannerDtoWire>>(`${BASE}/${id}`)));
  },

  async create(input: BannerInput): Promise<Banner> {
    const body: CreateBannerRequest = {
      ...toRequestBody(input),
      ...(input.languageId ? { languageId: input.languageId } : {}),
    };
    return mapBanner(unwrap(await http.post<ActionResponse<BannerDtoWire>>(BASE, body)));
  },

  async update(id: string, input: BannerInput): Promise<Banner> {
    const body: UpdateBannerRequest = toRequestBody(input);
    unwrap(await http.put<ActionResponse<boolean>>(`${BASE}/${id}`, body));
    return bannersApi.get(id);
  },

  async remove(id: string): Promise<void> {
    unwrap(await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`));
  },

  async setPublished(id: string, publish: boolean): Promise<void> {
    unwrap(
      await http.post<ActionResponse<boolean>>(`${BASE}/${id}/publish`, null, {
        params: { publish },
      }),
    );
  },
};
